import React, { useEffect, useMemo, useState } from 'react'
import {
  FormControl,
  Select,
  MenuItem,
  Chip,
  Box,
  Tooltip,
  type SelectChangeEvent,
} from '@mui/material'
import { apiClient } from '../lib/api'

/**
 * Supported DMS provider identifiers.
 * Extend this union as new providers are onboarded (CDK, Reynolds, Tekion, etc.).
 */
export type ProviderOption = 'opentrack' | 'dealer_vault'

const PROVIDER_LABELS: Record<ProviderOption, string> = {
  opentrack: 'OpenTrack',
  dealer_vault: 'DealerVault',
}

const ALL_PROVIDERS: ProviderOption[] = ['opentrack', 'dealer_vault']

type ProviderStats = Record<ProviderOption, number> & { total: number }
const EMPTY_STATS: ProviderStats = { opentrack: 0, dealer_vault: 0, total: 0 }

export interface ProviderSelectProps {
  value: ProviderOption
  onChange: (value: ProviderOption) => void
  dateRange: { startDate: string; endDate: string }
  tenantId?: string
  loading?: boolean
}

/**
 * ProviderSelect — dropdown for choosing a DMS data source.
 *
 * Discovers available providers via `/api/reports/provider-stats`.
 * Shows RO count badge only for the selected provider.
 * Disables when only one provider has data for the current tenant + date range.
 */
export const ProviderSelect: React.FC<ProviderSelectProps> = ({
  value,
  onChange,
  dateRange,
  tenantId,
  loading = false,
}) => {
  const [stats, setStats] = useState<ProviderStats>(EMPTY_STATS)
  const [fetching, setFetching] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const hasRange = Boolean(dateRange?.startDate) && Boolean(dateRange?.endDate)

  useEffect(() => {
    if (!hasRange) {
      setStats(EMPTY_STATS)
      return
    }

    let cancelled = false
    const params = new URLSearchParams({
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    })
    if (tenantId) params.set('tenantId', tenantId)

    setFetching(true)
    setError(null)

    apiClient
      .get(`/api/reports/provider-stats?${params.toString()}`)
      .then(async (res) => {
        if (!res.ok) {
          const text = await res.text().catch(() => '')
          throw new Error(text || `HTTP ${res.status}`)
        }
        return res.json()
      })
      .then((payload) => {
        if (cancelled) return
        setStats({
          opentrack: Number(payload?.opentrack ?? 0),
          dealer_vault: Number(payload?.dealervault ?? 0),
          total: Number(payload?.total ?? 0),
        })
      })
      .catch((err) => {
        if (cancelled) return
        console.warn('[ProviderSelect] provider-stats fetch failed', err)
        setStats(EMPTY_STATS)
        setError('Unable to load provider stats')
      })
      .finally(() => {
        if (!cancelled) setFetching(false)
      })

    return () => { cancelled = true }
  }, [dateRange.startDate, dateRange.endDate, tenantId, hasRange])

  const availableProviders = useMemo(
    () => ALL_PROVIDERS.filter((p) => stats[p] > 0),
    [stats],
  )

  const selectedCount = stats[value]
  const isDisabled = loading || fetching
  const onlyOneAvailable = availableProviders.length <= 1

  // Auto-switch if selected provider has no data but another does
  useEffect(() => {
    if (fetching) return
    if (stats[value] === 0 && availableProviders.length === 1) {
      onChange(availableProviders[0])
    }
  }, [stats, value, availableProviders, fetching, onChange])

  const handleChange = (event: SelectChangeEvent<string>) => {
    const next = event.target.value as ProviderOption
    if (next !== value) onChange(next)
  }

  return (
    <Tooltip
      title={error || (onlyOneAvailable ? 'Only one data source available' : '')}
      disableHoverListener={!error && !onlyOneAvailable}
    >
      <FormControl size="small" sx={{ minWidth: 170 }}>
        <Select
          value={value}
          onChange={handleChange}
          disabled={isDisabled || onlyOneAvailable}
          variant="outlined"
          sx={{
            fontSize: '0.8125rem',
            '& .MuiSelect-select': {
              py: '6px',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            },
          }}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>{PROVIDER_LABELS[selected as ProviderOption]}</span>
              {!fetching && selectedCount > 0 && (
                <Chip
                  label={`${selectedCount.toLocaleString()} ROs`}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              )}
              {fetching && (
                <Chip
                  label="…"
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              )}
            </Box>
          )}
        >
          {ALL_PROVIDERS.map((provider) => (
            <MenuItem
              key={provider}
              value={provider}
              disabled={!fetching && stats[provider] === 0}
            >
              {PROVIDER_LABELS[provider]}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Tooltip>
  )
}
